import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Cpu } from "lucide-react";
import PageHeader from "../components/sections/PageHeader";
import CTASection from "../components/sections/CTASection";
import { MACHINES } from "../lib/site";
import Seo from "../components/Seo";

const MachineRow = ({ m, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  const reverse = index % 2 === 1;

  return (
    <div
      ref={ref}
      id={m.slug}
      className="grid items-center gap-10 py-16 lg:grid-cols-2 lg:gap-16"
      data-testid={`machinery-row-${m.slug}`}
    >
      {/* Image */}
      <motion.div
        initial={{ opacity: 0, x: reverse ? 50 : -50 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className={`relative ${reverse ? "lg:order-2" : ""}`}
      >
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03]">
          <div className="spotlight absolute inset-0 z-10" />
          <img
            src={m.image}
            alt={m.name}
            loading="lazy"
            decoding="async"
            className={`relative z-0 w-full object-contain p-8 ${m.slug === "quality-inspection" ? "aspect-[3/4] max-h-[560px] object-cover object-top p-0" : "aspect-[4/3]"}`}
          />
          <div className="absolute left-5 top-5 z-20 rounded-full bg-brand px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
            {m.make}
          </div>
        </div>
      </motion.div>

      {/* Details */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-soft">
          <Cpu className="h-4 w-4" /> {m.model}
        </div>
        <h2 className="mt-4 font-display text-3xl font-semibold text-white sm:text-4xl">{m.name}</h2>
        <p className="mt-4 text-base leading-relaxed text-white/60">{m.description}</p>

        {/* Specs */}
        <div className="mt-7 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-3">
          {m.specs.map((s) => (
            <div key={s.k} className="bg-ink p-4">
              <div className="text-[10px] uppercase tracking-[0.16em] text-white/40">{s.k}</div>
              <div className="mt-1 font-display text-sm font-semibold text-white">{s.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-7 grid gap-6 sm:grid-cols-2">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-white/40">Applications</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {m.applications.map((a) => (
                <span key={a} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/70">{a}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-white/40">Benefits</div>
            <ul className="mt-3 space-y-2">
              {m.benefits.map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-white/70">
                  <Check className="h-4 w-4 text-brand-soft" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Machinery = () => {
  return (
    <div data-testid="machinery-page">
      <Seo
        title="Our Machinery"
        description="Explore PAHI's advanced manufacturing facility — AMADA NCT punching, 6kW fiber laser cutting, ISGEC power press, Hindustan Hydraulics press brake, Panasonic MIG/TIG welding, CNC/VMC, 9-stage powder coating & metrology-grade quality inspection."
        path="/machinery"
      />
      <PageHeader
        eyebrow="Manufacturing Facilities"
        crumb="Machinery"
        titleLines={["Our Machinery.", <span key="m" className="text-brand-soft">Precision, powered.</span>]}
        subtitle="A fully integrated facility equipped with advanced Japanese machinery — from fiber laser cutting to 9-stage powder coating — engineered for accuracy at scale."
      />

      <section className="relative overflow-hidden bg-ink grain">
        <div className="blueprint-grid absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-[1400px] divide-y divide-white/10 px-5 sm:px-8">
          {MACHINES.map((m, i) => (
            <MachineRow key={m.slug} m={m} index={i} />
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Machinery;
